import { AVALANCHE_C_CHAIN_ID, getChainConfig as getPluginChainConfig } from '@coti-io/coti-wallet-plugin';
import { cotiMainnetChain, cotiTestnetChain, COTI_TESTNET_CHAIN_ID } from './coti';
import { sepoliaChain } from './sepolia';
import { avalancheFujiChain, AVALANCHE_FUJI_CHAIN_ID } from './avalancheFuji';
import { ethereumMainnetChain, ETHEREUM_MAINNET_CHAIN_ID } from './ethereum';
import { withAvaxMainnetPortals } from './avalancheMainnetPortals';
import type { ChainConfig, TokenConfig, ResolvedIndexPageUi } from './types';

export type { ResolvedIndexPageUi };

export { AVALANCHE_FUJI_CHAIN_ID, AVALANCHE_C_CHAIN_ID, ETHEREUM_MAINNET_CHAIN_ID };

/**
 * Chain registry vendored from `@coti-io/coti-wallet-plugin` 0.4.0, which stopped
 * exporting `CHAIN_CONFIGS` from the package barrel. Backoffice readers and fee
 * simulation need the raw config (addresses, tokens, portal strategy, RPC fallbacks).
 *
 * Avalanche C-Chain is not snapshotted here — look it up from the installed plugin
 * via {@link getPluginChainConfig}.
 */
export const CHAIN_CONFIGS: Record<number, ChainConfig> = {
  [sepoliaChain.id]: sepoliaChain,
  [avalancheFujiChain.id]: avalancheFujiChain,
  [ethereumMainnetChain.id]: ethereumMainnetChain,
  [cotiTestnetChain.id]: cotiTestnetChain,
  [cotiMainnetChain.id]: cotiMainnetChain,
};

export const getChainConfig = (chainId?: number | string | null) => {
  const numericChainId = chainId == null ? undefined : Number(chainId);
  if (!numericChainId) return undefined;
  return withAvaxMainnetPortals(
    CHAIN_CONFIGS[numericChainId] ?? getPluginChainConfig(numericChainId),
  );
};

export const requireChainConfig = (chainId: number | string) => {
  const config = getChainConfig(chainId);
  if (!config) throw new Error(`Unsupported chain ID: ${chainId}`);
  return config;
};

export const getRpcUrlForChain = (chainId?: number | string | null) =>
  getChainConfig(chainId)?.rpcUrl ?? cotiTestnetChain.rpcUrl;

export const getRpcUrlsForChain = (chainId?: number | string | null): string[] => {
  const config = getChainConfig(chainId);
  if (!config) return [cotiTestnetChain.rpcUrl];
  return config.rpcFallbackUrls?.length
    ? [config.rpcUrl, ...config.rpcFallbackUrls]
    : [config.rpcUrl];
};

export const getTokensForChain = (chainId?: number | string | null): TokenConfig[] =>
  getChainConfig(chainId)?.tokens ?? [];

export const getPublicTokensForChain = (chainId?: number | string | null): TokenConfig[] =>
  getTokensForChain(chainId).filter((token) => !token.isPrivate);

export const getPrivateTokensForChain = (chainId?: number | string | null): TokenConfig[] =>
  getTokensForChain(chainId).filter((token) => token.isPrivate);

export const getNetworkNameForChain = (chainId?: number | string | null) =>
  getChainConfig(chainId)?.name ?? 'Wrong Network';

export const getExplorerBaseUrlForChain = (chainId?: number | string | null) =>
  getChainConfig(chainId)?.explorerBaseUrl ?? cotiTestnetChain.explorerBaseUrl;

export const resolveIndexPageUi = (chainId: number): ResolvedIndexPageUi => {
  const cfg = getChainConfig(chainId) ?? CHAIN_CONFIGS[COTI_TESTNET_CHAIN_ID];
  const ip = cfg.indexPage;
  const nativeSymbol = cfg.walletNetwork.nativeCurrency.symbol;
  return {
    showPodRequestTracker: ip.showPodRequestTracker,
    amountModalGasLabel: ip.amountModalGasLabel,
    amountModalGasSymbol: ip.amountModalGasSymbol === 'native' ? nativeSymbol : 'COTI',
  };
};

export { COTI_TESTNET_CHAIN_ID };
